/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridSortModel, GridPaginationModel } from "@mui/x-data-grid";
import { ChangeEvent, useCallback, useEffect } from 'react';
import { useAdminCampaignsService } from '../services';
import { CampaignConfig } from '../types';

export const useAdminCampaignsViewController = () => {

  const {
    isLoading,
    filters,
    campaignConfigs,
    totalCount,
    error,
    getCampaignConfigs,
    deleteCampaignConfig: deleteCampaignConfigService,
    updateCampaignConfig: updateCampaignConfigService,
    changeFilters,
  } = useAdminCampaignsService();

  const reloadCampaignConfigs = useCallback(async () => {
    try {
      await getCampaignConfigs();
    } catch (error) {
      console.error('Error reloading campaign configs:', error);
    }
  }, [getCampaignConfigs]);

  useEffect(() => {
    reloadCampaignConfigs();
  }, [reloadCampaignConfigs]);

  const changeSearchFilter = useCallback((_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeFilters({ query: query || undefined, offset: 0 });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const changeTypeFilter = useCallback((value: string) => {
    changeFilters({ types: value === '' ? undefined : value, offset: 0 });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const changeApplicationFilter = useCallback((value: string) => {
    // Note: applicationName sort is injected at API-call time (see AdminCampaignsService)
    // when applicationIds is set. Don't store it in filters.sort — that feeds the grid's
    // controlled sortModel and would round-trip back through onSortModelChange.
    changeFilters({ applicationIds: value === '' ? undefined : value, offset: 0, sort: undefined });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const changeCategoryFilter = useCallback((value: string) => {
    changeFilters({ categories: value === '' ? undefined : value, offset: 0, sort: undefined });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const changeSorting = useCallback((sort: GridSortModel) => {
    const sortString = sort.length > 0 ? `${sort[0].field}_${sort[0].sort}` : undefined;
    changeFilters({ sort: sortString, offset: 0 });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const changePagination = useCallback((p: GridPaginationModel) => {
    changeFilters({ offset: p.page * p.pageSize, limit: p.pageSize });
    reloadCampaignConfigs();
  }, [changeFilters, reloadCampaignConfigs]);

  const confirmDelete = useCallback(async (campaignConfig: CampaignConfig) => {
    try {
      await deleteCampaignConfigService(campaignConfig.id);
    } catch (error) {
      console.error('Error deleting campaign config:', error);
    }
  }, [deleteCampaignConfigService]);

  const confirmToggleStatus = useCallback(async (campaignConfig: CampaignConfig) => {
    try {
      const newStatus = campaignConfig.status === 'active' ? 'archived' : 'active';
      await updateCampaignConfigService(campaignConfig.id, {
        applicationIds: campaignConfig.applicationIds,
        config: typeof campaignConfig.config === 'string' ? campaignConfig.config : JSON.stringify(campaignConfig.config),
        status: newStatus
      });
    } catch (error) {
      console.error('Error toggling campaign config status:', error);
    }
  }, [updateCampaignConfigService]);

  const confirmUpdate = useCallback(async (id: string, data: { applicationIds: string[] | null; config: Record<string, any>; status: 'active' | 'archived' }) => {
    try {
      await updateCampaignConfigService(id, {
        applicationIds: data.applicationIds,
        config: JSON.stringify(data.config),
        status: data.status
      });
    } catch (error) {
      console.error('Error updating campaign config:', error);
    }
  }, [updateCampaignConfigService]);

  return {
    isPageLoading: isLoading,
    campaignConfigs,
    filters,
    totalCount,
    error,
    changeSearchFilter,
    changeTypeFilter,
    changeApplicationFilter,
    changeCategoryFilter,
    changeSorting,
    changePagination,
    confirmDelete,
    confirmToggleStatus,
    confirmUpdate,
  };
};
